import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('pointerdown', this.#handleTouchZoomPointerDown, { signal, capture: true });
    this.addEventListener('pointerup', this.#handleTouchZoomPointerUp, { signal, capture: true });
    this.addEventListener('pointercancel', this.#handleTouchZoomPointerCancel, { signal, capture: true });
  }

  #controller = new AbortController();
  #touchZoomStart = null;

  #handleTouchZoomPointerDown = (event) => {
    if (event.pointerType !== 'touch' || !event.isPrimary) return;
    const button =
      event.target instanceof Element ? event.target.closest('.product-media-container__touch-zoom-button') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    this.#touchZoomStart = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      button,
    };
  };

  #handleTouchZoomPointerUp = (event) => {
    const start = this.#touchZoomStart;
    this.#touchZoomStart = null;
    if (!start || event.pointerId !== start.pointerId) return;

    // Always suppress the synthetic click after a touch; taps are handled here and swipes remain swipes.
    const suppressClick = (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopImmediatePropagation();
    };
    start.button.addEventListener('click', suppressClick, { capture: true, once: true });
    window.setTimeout(() => start.button.removeEventListener('click', suppressClick, { capture: true }), 500);

    const distance = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    if (distance > 12) return;

    const index = Number.parseInt(start.button.dataset.zoomIndex ?? '', 10);
    if (!Number.isInteger(index)) return;

    event.preventDefault();
    event.stopPropagation();
    this.zoom(index, event);
  };

  #handleTouchZoomPointerCancel = () => {
    this.#touchZoomStart = null;
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const selectedMediaId = newMediaGallery.dataset.selectedMediaId;
        if (!selectedMediaId) return;

        const currentSlide =
          this.slideshow?.slides?.[this.slideshow.current];

        const currentMediaId =
          currentSlide?.getAttribute('slide-id');

        // The correct image is already displayed.
        if (currentMediaId === selectedMediaId) return;

        // Use Vessel's normal, subtle slideshow animation.
        this.slideshow?.select({ id: selectedMediaId });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[media-gallery] Event promise rejected:', error);
        }
      });
  };
  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
